import { styled } from '@pages/NewDashboard/Dashboard';
import {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridSizeChangedEvent,
  themeAlpine,
} from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ITableContainerProps } from 'interfaces/table.interface';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import style from './DeployModulesTables.module.scss';
import { OverlayComponent } from 'components/OverlayComponent/OverlayComponent';
import {
  IDeployModuleSearchRow,
  IDeployModuleServerRow,
} from 'interfaces/modules/module.interface';
import CellDeployModuleName from 'components/CellDeployModuleName/CellDeployModuleName';
import CellDeployModuleVersion from 'components/CellDeployModuleVersion/CellDeployModuleVersion';
import { AgGridField } from '@wk/components-react16';

const defaultColumnDef: ColDef = {
  width: 200,
  sortable: true,
  suppressColumnsToolPanel: true,
  icons: {
    sortAscending: '<span class="wk-icon-arrow-up" aria-hidden="true"/></span>',
    sortDescending:
      '<span class="wk-icon-arrow-down" aria-hidden="true"/></span>',
  },
};

interface IDeployModulesTablesProps extends ITableContainerProps {
  items: IDeployModuleSearchRow[] | IDeployModuleServerRow[];
  type: 'search-table' | 'server-table';
}

const gridOptionComponents = {
  cellDeployModuleName: CellDeployModuleName,
  cellDeployModuleVersion: CellDeployModuleVersion,
};

const DeployModulesTables = (props: IDeployModulesTablesProps): JSX.Element => {
  const gridApiRef = useRef<GridApi | null>(null);
  const gridColumnApiRef = useRef<any | null>(null);
  const [loading, setLoading] = useState(true);

  const updateLoadingOverlay = () => {
    if (gridApiRef.current) {
      if (loading) {
        gridApiRef.current.showLoadingOverlay();
      } else {
        gridApiRef.current.hideOverlay();
      }
    }
  };

  useEffect(() => {
    updateLoadingOverlay();
  }, [loading]);

  const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
    if (gridApiRef.current) {
      adjustColumnsWidth();
      setLoading(false);
    }
  };

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
    gridColumnApiRef.current = params.columnApi;
    setLoading(false);
  };

  const onGridSizeChanged = (event: GridSizeChangedEvent) => {
    gridApiRef.current?.sizeColumnsToFit();
  };

  const adjustColumnsWidth = () => {
    if (gridColumnApiRef) {
      gridColumnApiRef.current?.autoSizeAllColumns();
      gridApiRef.current?.sizeColumnsToFit();
    }
  };

  const getRowId = useCallback((params) => params.data.id, []);

  const gridOptions: GridOptions = {
    components: gridOptionComponents,
    suppressRowClickSelection: true,
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    rowBuffer: 20,
    localeText: {
      search: 'Start typing...',
    },
    defaultColDef: defaultColumnDef,
    columnDefs: props.columns,
    headerHeight: 40,
    rowHeight: 40,
    domLayout: 'normal',
    suppressCellFocus: true,
    loadingOverlayComponent: OverlayComponent,
    loadingOverlayComponentParams: {
      page: props.page,
      style: 'default',
      border: true,
    },
    overlayNoRowsTemplate: '<span>No results</span>',
    animateRows: true,
    onGridReady,
    onGridSizeChanged,
    onFirstDataRendered,
  };

  return (
    <CsbErrorBoundary>
      <div className={style['csb-table']}>
        <div
          className={style['wk-advanced-table-container']}
          data-testid="TableContainer"
          id="deploy-modules-table"
        >
          <div
            className={`wk-advanced-table ${style['csb-table-content']} ${props.additionalStyles}`}
          >
            <AgGridField>
              <AgGridReact
                theme={themeAlpine.withParams(styled)}
                gridOptions={gridOptions}
                rowData={props.items}
                getRowId={getRowId}
                {...props.additionalData}
              />
            </AgGridField>
          </div>
        </div>
      </div>
    </CsbErrorBoundary>
  );
};

export default DeployModulesTables;
